/**
 * UploadFlowSteps - Multi-step upload flow for story content
 * 
 * Step 1: Chapter Outlines (VERBATIM extraction - no AI)
 * Step 2: Character Profiles  
 * Step 3: World Rules (optional)
 * 
 * Each step has a SKIP button
 * Destiny video game inspired design
 */

import React, { useState, useRef } from 'react';
import { FileUp, BookOpen, Users, Globe, ChevronRight, SkipForward, Check, Sparkles, Star, Upload, RefreshCw } from 'lucide-react';
import { Button } from '../components/ui/button';
import { Card, CardContent } from '../components/ui/card';
import { Badge } from '../components/ui/badge';
import { toast } from 'sonner';
import axios from 'axios';

const API = '';

export default function UploadFlowSteps({ 
  onComplete, 
  onBack,
  setUploadedChapters,
  setUploadedCharacters,
  setUploadedWorld,
  uploadedChapters = [],
  uploadedCharacters = [],
  uploadedWorld = null,
  setAutoGeneratedStructure // New prop to pass back auto-generated story structure
}) {
  const [currentStep, setCurrentStep] = useState(1);
  const [isUploading, setIsUploading] = useState(false);
  const [uploadStatus, setUploadStatus] = useState('');
  const [showStoryCompletePrompt, setShowStoryCompletePrompt] = useState(false);
  const [storyStructure, setStoryStructure] = useState(null);
  
  // Activity log for debugging
  const [activityLog, setActivityLog] = useState([]);
  
  const addLog = (message, type = 'info') => {
    const timestamp = new Date().toLocaleTimeString();
    setActivityLog(prev => [...prev, { timestamp, message, type }]);
    console.log(`[${timestamp}] ${type.toUpperCase()}: ${message}`);
  };

  // Step definitions
  const steps = [
    {
      id: 1,
      title: 'Chapter Outlines',
      subtitle: 'Your story beats & chapter summaries',
      icon: BookOpen,
      description: 'Upload your chapter outlines, story beats, or scene descriptions. These will be copied VERBATIM - exactly as you wrote them.',
      hint: 'Supports: PDF, DOCX, TXT. Format: "Chapter 1: Title" followed by your content.',
      color: 'pink',
      data: uploadedChapters,
      setData: setUploadedChapters,
      endpoint: '/extract-chapters-verbatim',
      required: false
    },
    {
      id: 2,
      title: 'Character Profiles',
      subtitle: 'Your characters & their details',
      icon: Users,
      description: 'Upload character profiles, backstories, and relationships. Copied VERBATIM - your exact words.',
      hint: 'Format: **CHARACTER NAME** followed by Role:, Description:, Backstory:',
      color: 'cyan',
      data: uploadedCharacters,
      setData: setUploadedCharacters,
      endpoint: '/extract-characters-verbatim',
      category: 'characters',
      required: false
    },
    {
      id: 3,
      title: 'World Rules',
      subtitle: 'Magic systems, settings, laws',
      icon: Globe,
      description: 'Upload world-building details - magic systems, technology, society rules, settings.',
      hint: 'Optional: Skip if your story uses a real-world setting.',
      color: 'emerald',
      data: uploadedWorld,
      setData: setUploadedWorld,
      endpoint: '/smart-upload',
      category: 'world',
      required: false
    }
  ];

  const currentStepData = steps[currentStep - 1];
  const lastFilesRef = useRef(null);
  const [uploadFailed, setUploadFailed] = useState(false);

  // Calculate story structure by dividing chapters into thirds
  const calculateStoryStructure = (chapters) => {
    if (!chapters || chapters.length < 3) return null;
    
    const totalChapters = chapters.length;
    const beginningEnd = Math.floor(totalChapters / 3);
    const middleEnd = Math.floor((totalChapters * 2) / 3);
    
    // Get chapter summaries for each section
    const beginningChapters = chapters.slice(0, beginningEnd);
    const middleChapters = chapters.slice(beginningEnd, middleEnd);
    const endingChapters = chapters.slice(middleEnd);
    
    // Build summary text from chapter summaries
    const formatSection = (chapterList, label) => {
      const summaries = chapterList.map(ch => {
        const summary = ch.summary || ch.raw_text || '';
        return `Chapter ${ch.number}: ${ch.title || ''} - ${summary.substring(0, 200)}...`;
      }).join('\n');
      return summaries;
    };
    
    return {
      beginning: formatSection(beginningChapters, 'Beginning'),
      middle: formatSection(middleChapters, 'Middle'),
      ending: formatSection(endingChapters, 'Ending'),
      chapter_ranges: {
        beginning: `Chapters 1-${beginningEnd}`,
        middle: `Chapters ${beginningEnd + 1}-${middleEnd}`,
        ending: `Chapters ${middleEnd + 1}-${totalChapters}`
      }
    };
  };

  const handleStoryCompleteYes = () => {
    const structure = calculateStoryStructure(uploadedChapters);
    if (structure) {
      setStoryStructure(structure);
      if (setAutoGeneratedStructure) {
        setAutoGeneratedStructure(structure);
      }
      toast.success('Auto-calculated Beginning/Middle/End from your chapters!');
    }
    setShowStoryCompletePrompt(false);
  };

  const handleStoryCompleteNo = () => {
    setShowStoryCompletePrompt(false);
  };

  const handleFileUpload = async (files) => {
    if (!files || files.length === 0) return;

    lastFilesRef.current = files;
    setUploadFailed(false);
    setIsUploading(true);
    setUploadStatus('Running diagnostics...');
    
    // Clear previous logs and start fresh
    setActivityLog([]);
    addLog(`=== UPLOAD DIAGNOSTICS ===`);
    addLog(`API URL configured: ${API}`);
    addLog(`Target endpoint: ${currentStepData.endpoint}`);
    
    // PRE-FLIGHT CHECK 1: Test if backend is reachable
    addLog(`--- Pre-flight Check 1: Backend Health ---`);
    try {
      const healthResponse = await axios.get(`${API}/api/health`, { timeout: 10000 });
      addLog(`Health check: ${healthResponse.status} - ${JSON.stringify(healthResponse.data)}`, 'success');
    } catch (healthErr) {
      addLog(`Health check FAILED: ${healthErr.message}`, 'error');
      addLog(`Backend may be down or unreachable at: ${API}`, 'error');
    }
    
    // PRE-FLIGHT CHECK 2: Test if endpoint exists (OPTIONS/HEAD request)
    const fullUrl = `${API}/api${currentStepData.endpoint}`;
    addLog(`--- Pre-flight Check 2: Endpoint Exists ---`);
    addLog(`Testing: ${fullUrl}`);
    try {
      // Try an empty POST to see if endpoint exists (will return 422 for missing body, 404 if not found)
      const testResponse = await axios.post(fullUrl, new FormData(), { 
        timeout: 10000,
        validateStatus: (status) => true // Accept any status
      });
      if (testResponse.status === 404) {
        addLog(`ENDPOINT NOT FOUND (404) - This endpoint does not exist on the server!`, 'error');
        addLog(`The deployed backend version may be outdated`, 'error');
      } else if (testResponse.status === 422 || testResponse.status === 400) {
        addLog(`Endpoint exists (got ${testResponse.status} - expected for empty request)`, 'success');
      } else {
        addLog(`Endpoint responded with status: ${testResponse.status}`, 'info');
      }
    } catch (testErr) {
      addLog(`Endpoint test error: ${testErr.message}`, 'error');
    }
    
    addLog(`--- Starting Actual Upload ---`);
    setUploadStatus('Extracting content (verbatim copy)...');

    try {
      const formData = new FormData();
      for (let i = 0; i < files.length; i++) {
        formData.append('files', files[i]);
        addLog(`Added file: ${files[i].name} (${(files[i].size / 1024).toFixed(1)} KB)`);
      }

      // Use the appropriate endpoint
      const endpoint = currentStepData.endpoint;
      addLog(`Full URL: ${fullUrl}`);
      
      if (endpoint === '/extract-chapters-verbatim') {
        // Direct verbatim extraction for chapters - use longer timeout for large PDFs
        setUploadStatus('Processing your file... (this may take a moment for large PDFs)');
        addLog('Sending POST request for chapter extraction...');
        
        const response = await axios.post(fullUrl, formData, {
          timeout: 180000,  // 3 minutes for large files
          headers: {
            'Content-Type': 'multipart/form-data'
          },
          onUploadProgress: (progressEvent) => {
            const percentCompleted = Math.round((progressEvent.loaded * 100) / progressEvent.total);
            if (percentCompleted < 100) {
              setUploadStatus(`Uploading... ${percentCompleted}%`);
            } else {
              setUploadStatus('Processing chapters...');
              addLog('Upload complete, server processing...');
            }
          }
        });

        addLog(`Response status: ${response.status}`, 'success');
        addLog(`Response data: ${JSON.stringify(response.data).substring(0, 200)}...`);

        if (response.data.success) {
          const chapters = response.data.chapters || [];
          currentStepData.setData(chapters);
          toast.success(`Extracted ${chapters.length} chapters (verbatim)`);
          setUploadStatus(`✓ ${chapters.length} chapters extracted - your exact words`);
          addLog(`SUCCESS: Extracted ${chapters.length} chapters`, 'success');
          
          // If we have 3+ chapters, ask if the story is complete
          if (chapters.length >= 3) {
            setShowStoryCompletePrompt(true);
          }
        } else {
          toast.error(response.data.error || 'Failed to extract chapters');
          setUploadStatus('');
          addLog(`FAILED: ${response.data.error || 'Unknown error'}`, 'error');
        }
      } else if (endpoint === '/extract-characters-verbatim') {
        // Direct verbatim extraction for characters
        setUploadStatus('Processing your character profiles...');
        addLog('Sending POST request for character extraction...');
        
        const response = await axios.post(fullUrl, formData, {
          timeout: 180000,
          headers: {
            'Content-Type': 'multipart/form-data'
          },
          onUploadProgress: (progressEvent) => {
            const percentCompleted = Math.round((progressEvent.loaded * 100) / progressEvent.total);
            if (percentCompleted < 100) {
              setUploadStatus(`Uploading... ${percentCompleted}%`);
            } else {
              setUploadStatus('Extracting characters...');
              addLog('Upload complete, server processing...');
            }
          }
        });

        addLog(`Response status: ${response.status}`, 'success');
        addLog(`Response data: ${JSON.stringify(response.data).substring(0, 200)}...`);

        if (response.data.success) {
          const characters = response.data.characters || [];
          currentStepData.setData(characters);
          toast.success(`Extracted ${characters.length} characters (verbatim)`);
          setUploadStatus(`✓ ${characters.length} characters extracted - your exact profiles`);
          addLog(`SUCCESS: Extracted ${characters.length} characters`, 'success');
        } else {
          toast.error(response.data.error || 'Failed to extract characters');
          setUploadStatus('');
          setUploadFailed(true);
          addLog(`FAILED: ${response.data.error || 'Unknown error'}`, 'error');
        }
      } else {
        // Use smart upload for characters/world
        addLog('Using smart-upload endpoint...');
        if (currentStepData.category) {
          formData.append('category', currentStepData.category);
          addLog(`Category: ${currentStepData.category}`);
        }

        const response = await axios.post(`${API}/api/smart-upload`, formData, {
          timeout: 120000
        });

        addLog(`Response status: ${response.status}`, 'success');

        if (response.data.success) {
          const storyBible = response.data.story_bible || {};
          
          if (currentStepData.category === 'characters') {
            const chars = storyBible.characters || [];
            currentStepData.setData(chars);
            toast.success(`Extracted ${chars.length} characters`);
            setUploadStatus(`✓ ${chars.length} characters extracted`);
            addLog(`SUCCESS: Extracted ${chars.length} characters`, 'success');
          } else if (currentStepData.category === 'world') {
            currentStepData.setData(storyBible.world_building || {});
            toast.success('World rules extracted');
            setUploadStatus('✓ World rules extracted');
          }
        } else {
          toast.error('Failed to extract content');
          setUploadStatus('');
          setUploadFailed(true);
        }
      }
    } catch (error) {
      console.error('Upload error:', error);
      
      // Better error messages based on error type
      let errorMessage = 'Upload failed';
      if (error.code === 'ECONNABORTED' || error.message?.includes('timeout')) {
        errorMessage = 'Upload timed out - try a smaller file or check your connection';
      } else if (error.code === 'ERR_NETWORK' || error.message?.includes('Network')) {
        errorMessage = 'Network error - please check your internet connection and try again';
      } else if (error.response?.data?.detail) {
        errorMessage = error.response.data.detail;
      } else if (error.response?.data?.error) {
        errorMessage = error.response.data.error;
      } else if (error.message) {
        errorMessage = error.message;
      }
      
      // Log the full error details
      addLog(`ERROR: ${errorMessage}`, 'error');
      addLog(`Error code: ${error.code || 'none'}`, 'error');
      addLog(`Response status: ${error.response?.status || 'no response'}`, 'error');
      if (error.response?.data) {
        addLog(`Response data: ${JSON.stringify(error.response.data).substring(0, 300)}`, 'error');
      }
      
      toast.error(errorMessage);
      setUploadStatus('Upload failed - please try again');
      setUploadFailed(true);
    } finally {
      setIsUploading(false);
    }
  };

  const handleNext = () => {
    if (currentStep < steps.length) {
      setCurrentStep(currentStep + 1);
      setUploadStatus('');
      setUploadFailed(false);
      setShowStoryCompletePrompt(false); // Reset prompt when moving to next step
    } else {
      // Complete the flow - include auto-generated story structure if available
      onComplete({
        chapters: uploadedChapters,
        characters: uploadedCharacters,
        world: uploadedWorld,
        storyStructure: storyStructure // Include auto-calculated structure
      });
    }
  };

  const handleSkip = () => {
    handleNext();
  };

  const getColorClasses = (color) => {
    const colors = {
      pink: {
        border: 'border-pink-500/40',
        bg: 'from-pink-950/60 via-fuchsia-950/40 to-purple-950/30',
        hover: 'hover:border-pink-400/60',
        icon: 'bg-pink-500/20 text-pink-300',
        button: 'bg-pink-600 hover:bg-pink-500',
        text: 'text-pink-100',
        muted: 'text-pink-200/60',
        accent: 'text-pink-400'
      },
      cyan: {
        border: 'border-cyan-500/40',
        bg: 'from-cyan-950/60 via-blue-950/40 to-indigo-950/30',
        hover: 'hover:border-cyan-400/60',
        icon: 'bg-cyan-500/20 text-cyan-300',
        button: 'bg-cyan-600 hover:bg-cyan-500',
        text: 'text-cyan-100',
        muted: 'text-cyan-200/60',
        accent: 'text-cyan-400'
      },
      emerald: {
        border: 'border-emerald-500/40',
        bg: 'from-emerald-950/60 via-teal-950/40 to-green-950/30',
        hover: 'hover:border-emerald-400/60',
        icon: 'bg-emerald-500/20 text-emerald-300',
        button: 'bg-emerald-600 hover:bg-emerald-500',
        text: 'text-emerald-100',
        muted: 'text-emerald-200/60',
        accent: 'text-emerald-400'
      }
    };
    return colors[color] || colors.cyan;
  };

  const colors = getColorClasses(currentStepData.color);
  const StepIcon = currentStepData.icon;

  const hasData = currentStepData.data && 
    (Array.isArray(currentStepData.data) ? currentStepData.data.length > 0 : Object.keys(currentStepData.data).length > 0);

  return (
    <div className="space-y-6">
      {/* Header with inspiring message */}
      <div className="text-center space-y-2">
        <div className="flex items-center justify-center gap-2">
          <Sparkles className="w-5 h-5 text-pink-400" />
          <h2 className="text-xl font-bold text-white">Upload Your Creation</h2>
          <Sparkles className="w-5 h-5 text-pink-400" />
        </div>
        <p className="text-sm text-gray-400">
          Let Shweebum help bring your ideas to life
        </p>
      </div>

      {/* Progress indicators */}
      <div className="flex justify-center gap-2 mb-4">
        {steps.map((step, idx) => (
          <div 
            key={step.id}
            className={`flex items-center gap-2 px-3 py-1.5 rounded-full transition-all ${
              currentStep === step.id 
                ? `bg-${step.color}-500/20 border border-${step.color}-500/40` 
                : currentStep > step.id
                  ? 'bg-green-500/20 border border-green-500/40'
                  : 'bg-gray-800/50 border border-gray-700/40'
            }`}
          >
            {currentStep > step.id ? (
              <Check className="w-4 h-4 text-green-400" />
            ) : (
              <step.icon className={`w-4 h-4 ${currentStep === step.id ? `text-${step.color}-400` : 'text-gray-500'}`} />
            )}
            <span className={`text-xs font-medium ${
              currentStep === step.id ? 'text-white' : currentStep > step.id ? 'text-green-300' : 'text-gray-500'
            }`}>
              {step.title}
            </span>
          </div>
        ))}
      </div>

      {/* Current step card */}
      <Card className={`relative overflow-hidden border ${colors.border} bg-gradient-to-br ${colors.bg} ${colors.hover} transition-all duration-300`}>
        {/* Decorative stars */}
        <div className="absolute inset-0 overflow-hidden pointer-events-none">
          <Star className={`absolute top-3 right-4 w-3 h-3 ${colors.muted} animate-pulse`} />
          <Star className="absolute top-6 right-10 w-2 h-2 text-white/20 animate-pulse" style={{animationDelay: '0.5s'}} />
          <Star className={`absolute bottom-4 left-6 w-2.5 h-2.5 ${colors.muted} animate-pulse`} style={{animationDelay: '1s'}} />
        </div>

        <CardContent className="py-6 px-6 relative z-10">
          {/* Step header */}
          <div className="text-center mb-6">
            <div className={`w-14 h-14 mx-auto rounded-xl ${colors.icon} flex items-center justify-center mb-3 shadow-lg`}>
              <StepIcon className="w-7 h-7" />
            </div>
            <h3 className={`text-lg font-bold ${colors.text}`}>
              Step {currentStep}: {currentStepData.title}
            </h3>
            <p className={`text-sm ${colors.muted} mt-1`}>
              {currentStepData.subtitle}
            </p>
          </div>

          {/* Description */}
          <p className={`text-sm ${colors.text} text-center mb-4`}>
            {currentStepData.description}
          </p>

          {/* Upload zone */}
          <div 
            className={`border-2 border-dashed ${colors.border} rounded-xl p-6 text-center cursor-pointer ${colors.hover} hover:bg-white/5 transition-all ${isUploading ? 'opacity-50 pointer-events-none' : ''}`}
            onClick={() => document.getElementById(`upload-step-${currentStep}`)?.click()}
          >
            <input
              id={`upload-step-${currentStep}`}
              type="file"
              accept=".pdf,.txt,.docx"
              multiple
              className="hidden"
              onChange={(e) => handleFileUpload(e.target.files)}
            />
            
            {hasData ? (
              <div className="space-y-2">
                <Check className="w-10 h-10 mx-auto text-green-400" />
                <p className="text-green-300 font-medium">
                  {Array.isArray(currentStepData.data) 
                    ? `${currentStepData.data.length} items uploaded`
                    : 'Content uploaded'}
                </p>
                <p className="text-xs text-gray-400">Click to upload more or replace</p>
              </div>
            ) : (
              <div className="space-y-2">
                <Upload className={`w-10 h-10 mx-auto ${colors.accent}`} />
                <p className={colors.text}>Click to upload files</p>
                <p className="text-xs text-gray-400">{currentStepData.hint}</p>
              </div>
            )}
          </div>

          {/* Upload status */}
          {uploadStatus && (
            <p className={`text-sm text-center mt-3 ${uploadStatus.includes('✓') ? 'text-green-400' : colors.muted}`}>
              {uploadStatus}
            </p>
          )}

          {/* Retry button when upload fails */}
          {uploadFailed && !isUploading && (
            <div className="flex justify-center mt-3">
              <button
                data-testid="retry-upload-btn"
                onClick={() => {
                  if (lastFilesRef.current) {
                    handleFileUpload(lastFilesRef.current);
                  }
                }}
                className="flex items-center gap-2 px-4 py-2 rounded-lg bg-amber-600/80 hover:bg-amber-500/80 text-white text-sm font-medium transition-colors"
              >
                <RefreshCw className="w-4 h-4" />
                Retry Upload
              </button>
            </div>
          )}

          {/* Preview of uploaded data (for chapters) */}
          {currentStep === 1 && uploadedChapters.length > 0 && (
            <div className="mt-4 max-h-40 overflow-y-auto space-y-2 bg-black/20 rounded-lg p-3">
              <p className="text-xs text-gray-400 mb-2">Preview (first 3 chapters):</p>
              {uploadedChapters.slice(0, 3).map((ch, idx) => (
                <div key={idx} className="bg-black/30 rounded p-2">
                  <p className="text-sm font-medium text-pink-200">
                    Ch {ch.number}: {ch.title}
                  </p>
                  <p className="text-xs text-gray-400 truncate">
                    {ch.summary?.substring(0, 100)}...
                  </p>
                </div>
              ))}
              {uploadedChapters.length > 3 && (
                <p className="text-xs text-gray-500 text-center">
                  + {uploadedChapters.length - 3} more chapters
                </p>
              )}
            </div>
          )}

          {/* "Is this story complete?" prompt */}
          {showStoryCompletePrompt && currentStep === 1 && (
            <div className="mt-4 p-4 bg-gradient-to-br from-slate-800/60 via-slate-800/40 to-slate-700/30 border border-cyan-500/40 rounded-xl">
              <div className="flex items-center gap-2 mb-3">
                <Sparkles className="w-5 h-5 text-cyan-400" />
                <h4 className="font-semibold text-cyan-100">Is this story outline complete?</h4>
              </div>
              <p className="text-sm text-cyan-200/70 mb-4">
                If yes, we'll automatically calculate your story's Beginning, Middle, and End sections from your {uploadedChapters.length} chapters.
              </p>
              <div className="flex gap-3">
                <Button
                  onClick={handleStoryCompleteYes}
                  className="flex-1 bg-cyan-600 hover:bg-cyan-500 text-white"
                  data-testid="story-complete-yes-btn"
                >
                  <Check className="w-4 h-4 mr-2" />
                  Yes, Auto-Calculate
                </Button>
                <Button
                  variant="outline"
                  onClick={handleStoryCompleteNo}
                  className="flex-1 border-cyan-600/50 text-cyan-200 hover:bg-slate-800/40"
                  data-testid="story-complete-no-btn"
                >
                  No, I'll do it manually
                </Button>
              </div>
            </div>
          )}

          {/* Show auto-generated structure if calculated */}
          {storyStructure && currentStep === 1 && (
            <div className="mt-4 p-4 bg-gradient-to-br from-green-950/60 via-emerald-950/40 to-teal-950/30 border border-green-500/40 rounded-xl">
              <div className="flex items-center gap-2 mb-3">
                <Check className="w-5 h-5 text-green-400" />
                <h4 className="font-semibold text-green-100">Story Structure Auto-Calculated!</h4>
              </div>
              <div className="space-y-2 text-sm">
                <div className="bg-black/20 rounded p-2">
                  <span className="text-green-300 font-medium">Beginning:</span>
                  <span className="text-green-200/70 ml-2">{storyStructure.chapter_ranges.beginning}</span>
                </div>
                <div className="bg-black/20 rounded p-2">
                  <span className="text-green-300 font-medium">Middle:</span>
                  <span className="text-green-200/70 ml-2">{storyStructure.chapter_ranges.middle}</span>
                </div>
                <div className="bg-black/20 rounded p-2">
                  <span className="text-green-300 font-medium">Ending:</span>
                  <span className="text-green-200/70 ml-2">{storyStructure.chapter_ranges.ending}</span>
                </div>
              </div>
              <p className="text-xs text-green-400/70 mt-2">
                This will be used as your story structure. You can edit it later.
              </p>
            </div>
          )}

          {/* Activity Log - Debug Panel */}
          {activityLog.length > 0 && (
            <div className="mt-4 p-3 bg-black/40 border border-gray-700 rounded-lg">
              <div className="flex items-center justify-between mb-2">
                <h4 className="text-xs font-semibold text-gray-300">Activity Log (Debug)</h4>
                <div className="flex gap-2">
                  <button 
                    onClick={() => {
                      const logText = activityLog.map(l => `[${l.timestamp}] ${l.message}`).join('\n');
                      navigator.clipboard.writeText(logText);
                      toast.success('Log copied to clipboard');
                    }}
                    className="text-xs text-blue-400 hover:text-blue-300"
                  >
                    Copy
                  </button>
                  <button 
                    onClick={() => setActivityLog([])}
                    className="text-xs text-gray-500 hover:text-gray-300"
                  >
                    Clear
                  </button>
                </div>
              </div>
              <div className="max-h-32 overflow-y-auto space-y-1 font-mono text-xs">
                {activityLog.map((log, idx) => (
                  <div 
                    key={idx} 
                    className={`${
                      log.type === 'error' ? 'text-red-400' : 
                      log.type === 'success' ? 'text-green-400' : 
                      'text-gray-400'
                    }`}
                  >
                    <span className="text-gray-600">[{log.timestamp}]</span> {log.message}
                  </div>
                ))}
              </div>
            </div>
          )}

          {/* Action buttons */}
          <div className="flex gap-3 mt-6">
            <Button
              variant="outline"
              onClick={handleSkip}
              className="flex-1 border-gray-600 text-gray-300 hover:bg-gray-800"
              disabled={isUploading}
              data-testid="upload-skip-btn"
            >
              <SkipForward className="w-4 h-4 mr-2" />
              Skip
            </Button>
            <Button
              onClick={handleNext}
              className={`flex-1 relative overflow-hidden bg-gradient-to-r from-cyan-500 via-blue-500 to-violet-500 hover:from-cyan-400 hover:via-blue-400 hover:to-violet-400 text-white font-semibold shadow-lg shadow-cyan-500/25 hover:shadow-cyan-500/40 transition-all duration-300`}
              disabled={isUploading}
              data-testid="upload-next-btn"
              style={{
                boxShadow: '0 0 20px rgba(236, 72, 153, 0.3), 0 0 40px rgba(168, 85, 247, 0.15)'
              }}
            >
              <span className="relative z-10 flex items-center justify-center">
                {currentStep === steps.length ? 'Finish' : 'Next'}
                <ChevronRight className="w-4 h-4 ml-1" />
              </span>
            </Button>
          </div>
        </CardContent>
      </Card>

      {/* Back button */}
      <div className="text-center">
        <Button
          variant="ghost"
          onClick={onBack}
          className="text-gray-400 hover:text-white"
        >
          ← Back to options
        </Button>
      </div>
    </div>
  );
}
